import { Component, Input, HostListener, HostBinding, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';  

@Component({
    selector: 'hex-cell',
    standalone: true,
    imports: [ CommonModule ],
    templateUrl: './hex-cell.html',
    styleUrl: './hex-cell.scss',
})

export class HexCell {
    bgImage: string | null = null;
    @Input() url: string | null = null;
    @Input() delay: number = 1000;
    @Input() set background(value: string | null) {
        if (value) {
            this.bgImage = value;
        }
    }
    @HostBinding('style.yz-index') zIndex = 0;
    @ViewChild('anchor', { read: ElementRef }) anchorRef!: ElementRef<HTMLAnchorElement>;
    @ViewChild('hexcell', { read: ElementRef }) hexcellRef!: ElementRef<HTMLDivElement>;
    private isHovered = false;
    private isFocused = false;

    constructor(private el: ElementRef) {}

    hexPathRounded() {
        const points = [];
        const sides = 6;
        const centerX = 0.5;
        const centerY = 0.5;
        const radiusX = 0.49;
        const radiusY = 0.49 / 0.875;
        const borderRadius = 0.17;
        const piPul = Math.PI / 2;
        const piDve = Math.PI * 2;

        // 1. Calculate polygon vertices
        const vertices = [];
        for (let i = 0; i < sides; i++) {
            const angle = (i / sides) * piDve - piPul;
            vertices.push({
            x: centerX + radiusX * Math.sin(angle),
            y: centerY + radiusY * Math.cos(angle)
            });
        }
        
        for (let i = 0; i < sides; i++) {
            const prev = vertices[(i + sides - 1) % sides];
            const curr = vertices[i];
            const next = vertices[(i + 1) % sides];

            // 2. Calculate rounded corner edge points (Corner Arc end points)
            const x1 = curr.x + (prev.x - curr.x) * borderRadius;
            const y1 = curr.y + (prev.y - curr.y) * borderRadius;
            const x2 = curr.x + (next.x - curr.x) * borderRadius;
            const y2 = curr.y + (next.y - curr.y) * borderRadius;

            if (i === 0) {
                // First point
                points.push(`M ${x1.toFixed(3)},${y1.toFixed(3)}`);
            } else {
                // Connecting previous point
                points.push(`L ${x1.toFixed(3)},${y1.toFixed(3)}`);
            }

            // Quadratic curve (rounded corner)
            points.push(`Q ${curr.x.toFixed(3)},${curr.y.toFixed(3)} ${x2.toFixed(3)},${y2.toFixed(3)}`);
        }
        return points.join(' ') + ' Z';            
    }

    @HostListener('click', ['$event'])
    onClick(event: Event) {
        if(event.target != this.anchorRef.nativeElement)
            return;

        event.preventDefault();
        event.stopPropagation();

        this.el.nativeElement.classList.add('is-active');
        this.hexcellRef.nativeElement.classList.add('is-active');

        setTimeout(() => {
            if (this.url) {
                window.location.href = this.url;
            }
        }, this.delay);
    }

    private updateZIndex() {
        const elevated = this.isHovered || this.isFocused;
        this.zIndex = elevated ? 3 : 0;
        setTimeout(() => {
            const elevated = this.isHovered || this.isFocused;
            this.zIndex = elevated ? 3 : 0;
        }, this.delay);
    }

    @HostListener('mouseenter') onMouseEnter() {
        this.isHovered = true;
        this.zIndex = 3;
    }
    @HostListener('mouseleave') onMouseLeave() {
        this.isHovered = false;
        this.updateZIndex();
    }
    @HostListener('focusin') onFocus() {
        this.isFocused = true;
        this.zIndex = 3;
    }
    @HostListener('focusout') ofFocusOut() {
        this.isFocused = false;
        this.updateZIndex();
    }

    private resetComponentState() {
        this.zIndex = 0;
        this.isHovered = false;
        this.isFocused = false;

        if (this.hexcellRef) {
            this.hexcellRef.nativeElement.classList.remove('is-active');
        }
        this.el.nativeElement.classList.remove('is-active');
    }

    @HostListener('window:pageshow', ['$event'])
    onPageShow(event: PageTransitionEvent) {
        if (event.persisted || (window.performance && window.performance.navigation.type === 2)) {
            this.resetComponentState();
        }
    }
}